import {
  Box,
  Select,
  MenuItem,
  Typography,
  Button,
  styled,
  Divider,
} from '@mui/material'
import PageWrapper from 'hoc/PageWrapper'
import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PublishForm, PublishFormData } from './publish-form'
import { ProcurementForm } from './procurement-form'
import { ProcurementPlansTable } from './procurement-plans-table'
import { Download } from '@mui/icons-material'
import { FileUpload } from 'components/FileUpload'
import readXlsxFile from 'read-excel-file'
import { IProcurementPlanItem } from 'types/procurementPlans/procurementPlanItems'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import i18n from 'i18n'
// @ts-ignore
import Excel from './procurement_plan_template.xlsx'
import { IFile } from 'types/file'
import { usePublishProcurementPlanMutation } from 'services/procurementPlans'
import { EProcurementPlanChangeReason } from 'types/procurementPlans/procurementPlanLogs'
import { EProcurementPlanStatus } from 'types/procurementPlans'
import { isValidRow, rowToProcurementPlanItem } from 'utils/procurement-plan'
import { formattedTimeStandart } from 'hooks/date/formattedTime'

const StyledSelect = styled(Select)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  '.MuiSelect-iconOutlined': {
    color: '#FFFFFF',
  },
}))

export const CreateProcurementPlan: FunctionComponent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [publishProcurementPlan, { isLoading: isCreating, isSuccess }] =
    usePublishProcurementPlanMutation()
  const [searchParams] = useSearchParams()
  const year = searchParams.get('year')
  const [publishFormVisible, setPublishFormVisible] = useState(false)
  const [procurementFormVisible, setProcurementFormVisible] = useState(false)
  const [procurementPlans, setProcurementPlans] = useState<IProcurementPlanItem[]>(
    []
  )
  const [currentlyEditingPlan, setCurrentlyEditingPlan] =
    useState<IProcurementPlanItem | null>(null)

  useEffect(() => {
    if (isSuccess) {
      navigate('/procurement-plans')
    }
  }, [isSuccess, navigate])

  const handlePublishClick = () => {
    setPublishFormVisible(true)
  }

  const handleCreateEditModalClose = () => {
    setProcurementFormVisible(false)
    setCurrentlyEditingPlan(null)
  }

  const handleMissingFileAddition = (plan: IProcurementPlanItem, file: IFile) => {
    setProcurementPlans((old) =>
      old.map((i) => {
        if (i.id === plan.id) {
          return {
            ...i,
            procurementTable: file,
          }
        }

        return plan
      })
    )
  }

  const handleCreate = (publishData: PublishFormData) => {
    setPublishFormVisible(false)
    publishProcurementPlan({
      archive: false,
      body: {
        id: uuidv4(),
        year: Number(year),
        changeReason: publishData.changeReason as EProcurementPlanChangeReason,
        reasonDescription: publishData.reasonDescription,
        creationOrderDate: formattedTimeStandart(publishData.creationOrderDate),
        creationOrderFileId: publishData.file?.id,
        archived: false,
        status: EProcurementPlanStatus.PUBLISHED,
        correlationId: uuidv4(),
        procurementPlanItems: procurementPlans.map((plan, index) => ({
          ...plan,
          id: uuidv4(),
          executionDurationInDays: plan.executionDurationInDays ?? '0',
        })),
      },
    })
  }

  if (!year) {
    navigate('/procurement-plans')
  }

  return (
    <PageWrapper>
      <Box display="flex" alignItems="center">
        <Typography variant="h4" textTransform="uppercase" marginRight={8}>
          {t('procurement-plan')} {year}
        </Typography>
        <Typography variant="body1">
          {t('status')}: {t('in-progress')}
        </Typography>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={2}
      >
        <StyledSelect
          value="in-progress"
          variant="outlined"
          color="primary"
          size="small"
          displayEmpty
        >
          <MenuItem value="in-progress">{t('in-progress')}</MenuItem>
          <MenuItem value="published">{t('published')}</MenuItem>
        </StyledSelect>

        <Box display="flex" gap={2}>
          <Button
            disabled={!procurementPlans.length || isCreating}
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handlePublishClick}
          >
            {t('publish')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={() => navigate(-1)}
          >
            {t('cancel')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box display="flex" justifyContent="flex-end" marginBottom={1.5} gap={2}>
        <Button
          color="primary"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          onClick={() => setProcurementFormVisible(true)}
        >
          {t('add-new-plan')}
        </Button>

        <Button variant="text" startIcon={<Download />}>
          <a style={{ textDecoration: 'none', color: 'inherit' }} href={Excel}>
            {t('download-template')}
          </a>
        </Button>

        <FileUpload
          text={t('add-sheet')}
          sendToApi={false}
          handleAsRawFile={(file) => {
            if (file) {
              readXlsxFile(file)
                .then((rows) => {
                  rows.shift()
                  if (!rows.every(isValidRow)) {
                    toast.warning(t('invalidProcurementPlanTemplateError'))
                    return
                  }

                  const procurementPlans = rows.map(rowToProcurementPlanItem)
                  setProcurementPlans((prevPlans) => [
                    ...prevPlans,
                    ...(procurementPlans as unknown as IProcurementPlanItem[]),
                  ])
                })
                .catch(() => {
                  toast.error(i18n.t('failedToCreate'))
                })
            }
          }}
        />
      </Box>

      <ProcurementPlansTable
        plans={procurementPlans}
        onEdit={(plan) => {
          setCurrentlyEditingPlan(plan)
          setProcurementFormVisible(true)
        }}
        onDelete={(planId) => {
          setProcurementPlans((prevPlans) => {
            return prevPlans.filter((plan) => plan.id !== planId)
          })
        }}
        onAddFile={handleMissingFileAddition}
      />

      {publishFormVisible && (
        <PublishForm
          onCreate={handleCreate}
          handleClose={() => setPublishFormVisible(false)}
        />
      )}
      {procurementFormVisible && (
        <ProcurementForm
          handleClose={handleCreateEditModalClose}
          onProcurementPlanItemCreate={(item) => {
            if (item.id) {
              setProcurementPlans((prevPlans) => [
                ...prevPlans.filter((i) => i.id !== item.id),
                { ...item },
              ])
            } else {
              setProcurementPlans((prevPlans) => [
                ...prevPlans,
                { ...item, id: uuidv4() },
              ])
            }
          }}
          plan={currentlyEditingPlan}
        />
      )}
    </PageWrapper>
  )
}
